import { useState, useEffect } from "react";
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { setCredentials } from "/src/store/slices/AuthSlice";
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { HiOutlineMail, HiOutlineLockClosed, HiOutlineEye, HiOutlineEyeOff, HiOutlineExclamationCircle, HiOutlineX } from 'react-icons/hi';

const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Invalid email address').required('Email is required'),
    password: Yup.string().required('Password is required')
});

export default function LoginPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [showPassword, setShowPassword] = useState(false);
    const [loginError, setLoginError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('Please check your email and password.');
    const [isLoading, setIsLoading] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
    
    useEffect(() => {
        if (location.state?.message) {
            setLoginError(true);
            setErrorMessage(location.state.message);
            
            navigate(location.pathname, { replace: true });
        }
    }, [location, navigate]);

    useEffect(() => {
        const html = document.querySelector('html');
        if (isDarkMode) {
            html.classList.add('dark');
            localStorage.setItem('darkMode', 'true');
        } else {
            html.classList.remove('dark');
            localStorage.setItem('darkMode', 'false');
        }
    }, [isDarkMode]);

    const toggleDarkMode = () => {
        setIsDarkMode(!isDarkMode);
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        setIsLoading(true);
        setLoginError(false);

        try {
            const response = await fetch(`${import.meta.env.VITE_SERVER_BASE_URL}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values),
            });
            
            if (!response.ok) {
                throw new Error('Invalid login');
            }
            
            const data = await response.json();
            
            dispatch(setCredentials({
                user: data.data.user,
                token: data.data.accessToken
            }));
            
            navigate('/blog/blog-dashboard');
        } catch (error) {
            setLoginError(true);
            setErrorMessage('Invalid credentials! Please check your email and password.');
        } finally {
            setIsLoading(false);
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-500 p-4">
            <div className="absolute top-4 right-4">
                <button 
                    onClick={toggleDarkMode}
                    className="p-2 rounded-full bg-white dark:bg-gray-800 shadow-md hover:shadow-lg transition-all duration-300"
                    aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
                >
                    {isDarkMode ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                        </svg>
                    )}
                </button>
            </div>
            
            <div className="w-full max-w-md">
                <div className="bg-white dark:bg-gray-800 shadow-2xl rounded-2xl overflow-hidden transform transition-all duration-300 hover:shadow-3xl">
                    <div className="bg-gradient-to-r p-6 flex justify-center">
                        <img 
                            src="/assets/images/fav-icons/favicon.svg" 
                            alt="LegsGo Holidays" 
                            className="h-16 w-auto filter drop-shadow-md animate-fadeIn" 
                        />
                    </div>
                    
                    <div className="p-8">
                        <div className="text-center mb-8">
                            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                                Welcome Back
                            </h2>
                            <p className="text-gray-600 dark:text-gray-300 mt-2">
                                Sign in to LegsGo Admin Dashboard
                            </p>
                        </div>
                        
                        {loginError && (
                            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg relative animate-shake" role="alert">
                                <div className="flex items-center">
                                    <HiOutlineExclamationCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                                    <div className="flex-1">
                                        <strong className="font-medium">Authentication Error! </strong>
                                        <span className="block sm:inline">{errorMessage}</span>
                                    </div>
                                    <button
                                        className="ml-auto" 
                                        onClick={() => setLoginError(false)}
                                        aria-label="Close alert"
                                    >
                                        <HiOutlineX className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        )}

                        <Formik
                            initialValues={{ email: '', password: '' }}
                            validationSchema={LoginSchema}
                            onSubmit={handleSubmit}
                        >
                            {({ errors, touched, isSubmitting }) => (
                                <Form className="space-y-6">
                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Email
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <HiOutlineMail className="h-5 w-5 text-gray-400" />
                                            </div>
                                            <Field
                                                name="email"
                                                type="email"
                                                className={`block w-full pl-10 pr-3 py-3 border ${errors.email && touched.email ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white transition-all duration-200`}
                                                placeholder="Enter your email address"
                                            />
                                            {errors.email && touched.email && (
                                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                                    <HiOutlineExclamationCircle className="h-5 w-5 text-red-500" aria-hidden="true" />
                                                </div>
                                            )}
                                        </div>
                                        {errors.email && touched.email && (
                                            <p className="mt-2 text-sm text-red-600 dark:text-red-400 animate-fadeIn">{errors.email}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Password
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <HiOutlineLockClosed className="h-5 w-5 text-gray-400" />
                                            </div>
                                            <Field
                                                name="password"
                                                type={showPassword ? "text" : "password"}
                                                className={`block w-full pl-10 pr-10 py-3 border ${errors.password && touched.password ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'} rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white transition-all duration-200`}
                                                placeholder="Enter your password"
                                            />
                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none"
                                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                                >
                                                    {showPassword ? (
                                                        <HiOutlineEyeOff className="h-5 w-5" />
                                                    ) : (
                                                        <HiOutlineEye className="h-5 w-5" />
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                        {errors.password && touched.password && (
                                            <p className="mt-2 text-sm text-red-600 dark:text-red-400 animate-fadeIn">{errors.password}</p>
                                        )}
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <Field
                                                type="checkbox"
                                                name="remember"
                                                id="remember"
                                                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded cursor-pointer"
                                            />
                                            <label htmlFor="remember" className="ml-2 block text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                                                Remember me
                                            </label>
                                        </div>
                                        <div className="text-sm">
                                            <a href="/forgot-password" className="font-medium text-primary hover:text-primary-dark dark:text-primary-light dark:hover:text-primary transition-colors duration-200">
                                                Forgot password?
                                            </a>
                                        </div>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={isSubmitting || isLoading}
                                        className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed relative overflow-hidden"
                                    >
                                        {isLoading ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Signing in...
                                            </>
                                        ) : (
                                            "Sign in"
                                        )}
                                    </button>
                                </Form>
                            )}
                        </Formik>

                        <div className="mt-8 text-center">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Don't have an account?{' '}
                                <a href="#" className="font-medium text-primary hover:text-primary-dark dark:text-primary-light dark:hover:text-primary transition-colors duration-200">
                                    Contact administrator
                                </a>
                            </p>
                        </div>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-750 px-8 py-4 text-center border-t border-gray-200 dark:border-gray-700">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            &copy; {new Date().getFullYear()} LegsGo Holidays. All rights reserved.
                        </p>
                    </div>
                </div>
            </div>
            
            {/* CSS for animations */}
            <style jsx>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                
                .animate-fadeIn {
                    animation: fadeIn 0.3s ease-in-out;
                }
                
                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
                    20%, 40%, 60%, 80% { transform: translateX(5px); }
                }
                
                .animate-shake {
                    animation: shake 0.5s linear;
                }
                
                /* Custom shadow for card hover */
                .shadow-3xl {
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                }
            `}</style>
        </div>
    );
}